import {singleton} from 'tsyringe';
import {
    TimelineLayout,
    TimelineLayoutConfig,
    TimelineLayoutRecalculationResult
} from '../timeline/timeline-layout.types.js';
import {TimelineSizeCalculator} from '../timeline/timeline-size.calculator.js';
import {DateTime} from "luxon";

/**
 * Service for calculating and managing timeline layout dimensions.
 * Centralizes all layout calculations to ensure consistency and testability.
 *
 * Follows KISS principles by providing simple, focused methods for layout management.
 */
@singleton()
export class TimelineLayoutService {

    private _lastLayout: TimelineLayout | null = null;

    /**
     * Calculate the complete timeline layout based on configuration
     *
     * @param config Configuration object with all necessary parameters
     * @returns Complete timeline layout object
     */
    public calculateLayout(config: TimelineLayoutConfig): TimelineLayout {
        if (!config.parentElement) {
            return this._createInvalidLayout();
        }

        // Calculate grid dimensions using existing calculator
        const {numberOfColumns, numberOfRows, deltaY} = TimelineSizeCalculator.calculate(
            config.parentElement,
            config.cellWidthMultiplication
        );

        // Calculate days distribution
        const adjustedNumberOfRows = numberOfRows - 4; // Header rows
        const daysAfter = numberOfColumns - config.daysBefore;

        // Calculate cell dimensions
        const cellHeight = TimelineSizeCalculator.calculateCellHeight(
            config.parentElement,
            adjustedNumberOfRows + 4
        );

        const cellWidth = TimelineSizeCalculator.calculateCellWidth(
            config.parentElement,
            config.daysBefore + daysAfter
        );

        // Calculate calendar boundaries
        const dateCursor = config.dateCursorDate;
        const firstDayOfCalendar = dateCursor.minus({days: config.daysBefore});
        const lastDayOfCalendar = dateCursor.plus({days: daysAfter});

        const layout: TimelineLayout = {
            numberOfRows: adjustedNumberOfRows,
            numberOfColumns,
            daysBefore: config.daysBefore,
            daysAfter,
            cellHeight,
            cellWidth,
            deltaY,
            firstDayOfCalendar,
            lastDayOfCalendar,
            isValid: true
        };

        // Pure function - no caching here!
        return layout;
    }

    /**
     * Recalculate layout for resize events.
     * This method ensures complete recalculation of all layout properties.
     *
     * @param config Updated configuration after resize
     * @returns Recalculation result with change detection
     */
    public recalculateForResize(config: TimelineLayoutConfig): TimelineLayoutRecalculationResult {
        // Save the current layout BEFORE calculating new one
        const previousLayout = this._lastLayout;

        // Calculate new layout (pure function, doesn't modify state)
        const newLayout = this.calculateLayout(config);

        if (!previousLayout) {
            // First calculation - cache the layout
            this._lastLayout = newLayout;
            return {
                layout: newLayout,
                hasChanged: true,
                changedProperties: Object.keys(newLayout) as Array<keyof TimelineLayout>
            };
        }

        // Detect changes between old and new layout
        const changedProperties = this._detectChanges(previousLayout, newLayout);
        const hasChanged = changedProperties.length > 0;

        // Only cache if there are actual changes
        if (hasChanged) {
            this._lastLayout = newLayout;
        }

        return {
            layout: newLayout,
            hasChanged,
            changedProperties
        };
    }

    /**
     * Manually cache a layout (used by views for initial calculation)
     *
     * @param layout Layout to cache
     */
    public cacheLayout(layout: TimelineLayout): void {
        this._lastLayout = layout;
    }

    /**
     * Clear cached layout (useful for testing or forced recalculation)
     */
    public clearCache(): void {
        this._lastLayout = null;
    }

    /**
     * Detect changes between two layouts
     *
     * @param oldLayout Previous layout
     * @param newLayout New layout to compare
     * @returns Array of property names that changed
     */
    private _detectChanges(oldLayout: TimelineLayout, newLayout: TimelineLayout): Array<keyof TimelineLayout> {
        const changes: Array<keyof TimelineLayout> = [];

        for (const key of Object.keys(newLayout) as Array<keyof TimelineLayout>) {
            const oldValue = oldLayout[key];
            const newValue = newLayout[key];

            // Special handling for Date objects
            if (oldValue instanceof DateTime && newValue instanceof DateTime) {
                if (!oldValue.equals(newValue)) {
                    changes.push(key);
                }
            } else if (oldValue !== newValue) {
                changes.push(key);
            }
        }

        return changes;
    }

    /**
     * Create an invalid layout object for error cases
     *
     * @returns Invalid layout with default values
     */
    private _createInvalidLayout(): TimelineLayout {
        const now = DateTime.utc();
        return {
            numberOfRows: 0,
            numberOfColumns: 0,
            daysBefore: 0,
            daysAfter: 0,
            cellHeight: 0,
            cellWidth: 0,
            deltaY: 0,
            firstDayOfCalendar: now,
            lastDayOfCalendar: now,
            isValid: false
        };
    }
}